import dayjs from "dayjs";

import { parseNumericValue } from "./numberFormatter";
import {
  getZar8YieldCalculatorAvailableBalanceZar,
  getZar8YieldCalculatorZar8Price,
} from "./zar8YieldCalculator";
import { buildZar8YieldPositionHistory } from "./zar8YieldPosition";

export const ZAR8_ASSET_PAGE_PRODUCT = "zar8_asset";
export const ZAR8_ASSET_DESCRIPTION =
  "ZAR8 is 80eight's South African rand-denominated digital asset, designed to give users a stable value reference inside the yield ecosystem. You can hold ZAR8 as a cash-equivalent balance, track its performance, and lock it into yield products to earn BTC-denominated rewards over time.";
export const ZAR8_ASSET_TIME_FILTERS = ["All", "1D", "1W", "1M", "3M", "1Y"];

export const ZAR8_ASSET_PRICE_HISTORY_CONFIG = {
  All: {
    interval: "1y",
  },
  "1D": {
    interval: "1d",
  },
  "1W": {
    interval: "1w",
  },
  "1M": {
    interval: "1m",
  },
  "3M": {
    interval: "3m",
  },
  "1Y": {
    interval: "1y",
  },
};

export const getZar8AssetPriceHistoryConfig = (timeFilter = "All") => {
  const configEntry = Object.entries(ZAR8_ASSET_PRICE_HISTORY_CONFIG).find(
    ([filter]) => filter === timeFilter
  );

  return configEntry?.[1] || ZAR8_ASSET_PRICE_HISTORY_CONFIG.All;
};

export const getMarketAssetBySymbol = (marketAssets, symbol) => {
  if (!marketAssets || !symbol) {
    return null;
  }

  const normalizedSymbol = `${symbol}`.toUpperCase();
  const entries = Object.entries(marketAssets);
  const directMatch = entries.find(
    ([key]) => key?.toUpperCase() === normalizedSymbol
  );

  return (
    (directMatch ? directMatch[1] : null) ||
    entries.find(
      (entry) =>
        entry[1]?.symbol?.toUpperCase() === normalizedSymbol ||
        entry[1]?.info?.asset?.toUpperCase() === normalizedSymbol
    )?.[1] ||
    null
  );
};

export const getZar8AssetBalanceValue = ({ zar8Asset, marketAssets }) => {
  const explicitValue = getZar8YieldCalculatorAvailableBalanceZar(zar8Asset);

  if (explicitValue > 0) {
    return explicitValue;
  }

  const zar8Balance = parseNumericValue(zar8Asset?.availableAmount);
  const marketAsset = getMarketAssetBySymbol(marketAssets, "ZAR8");
  const priceInZar = parseNumericValue(
    marketAsset?.priceInZar ?? marketAsset?.info?.zarprice ?? marketAsset?.price
  );

  return zar8Balance * (priceInZar > 0 ? priceInZar : 1);
};

const getShortLabelFormat = (timeFilter) => {
  if (timeFilter === "1D") {
    return "HH:mm";
  }

  if (timeFilter === "1M") {
    return "DD MMM";
  }

  return "MMM";
};

const getFullLabelFormat = (timeFilter) => {
  if (timeFilter === "1D") {
    return "D MMM YYYY HH:mm";
  }

  return "D MMM YYYY";
};

const getShortWeekdayLabel = (dateValue) => {
  const weekday = dateValue.format("ddd");

  return weekday === "Thu" ? "Thur" : weekday;
};

const getTooltipLabel = (dateValue, timeFilter) => {
  if (!dateValue.isValid()) {
    return "";
  }

  if (timeFilter === "1D") {
    return dateValue.format("HH:mm");
  }

  if (timeFilter === "1W" || timeFilter === "1M") {
    return dateValue.format("D MMM");
  }

  return dateValue.format(getFullLabelFormat(timeFilter));
};

export const buildZar8AssetPerformanceSeries = ({
  priceHistoryData,
  timeFilter = "All",
  zar8Asset = null,
  marketAssets = null,
}) => {
  const historyPoints = Array.isArray(priceHistoryData?.prices)
    ? priceHistoryData.prices
    : [];

  if (historyPoints.length === 0) {
    return [];
  }

  const zar8Balance = parseNumericValue(zar8Asset?.availableAmount);
  const zar8PriceInZar = getZar8YieldCalculatorZar8Price(zar8Asset);
  const fallbackMultiplier = zar8Balance > 0 ? zar8Balance : 1;
  const marketAsset = getMarketAssetBySymbol(marketAssets, "ZAR8");
  const liveMarketPriceInZar = parseNumericValue(
    marketAsset?.priceInZar ?? marketAsset?.info?.zarprice ?? marketAsset?.price
  );

  const normalizedCurrentPrice =
    liveMarketPriceInZar > 0 ? liveMarketPriceInZar : zar8PriceInZar || 1;

  return historyPoints.map((point, index) => {
    const timestamp = point?.time ?? point?.timestamp ?? point?.date;
    const dateValue = dayjs(timestamp);
    const rawPrice = parseNumericValue(point?.price ?? point?.value);
    const normalizedPrice =
      rawPrice > 0 ? rawPrice : normalizedCurrentPrice || zar8PriceInZar || 1;
    const holdingsValue = normalizedPrice * fallbackMultiplier;

    return {
      id: `${timestamp || index}`,
      value: holdingsValue,
      shortLabel: dateValue.isValid()
        ? timeFilter === "1W"
          ? getShortWeekdayLabel(dateValue)
          : dateValue.format(getShortLabelFormat(timeFilter))
        : `${index + 1}`,
      fullLabel: dateValue.isValid()
        ? dateValue.format(getFullLabelFormat(timeFilter))
        : `Point ${index + 1}`,
      tooltipLabel:
        getTooltipLabel(dateValue, timeFilter) || `Point ${index + 1}`,
    };
  });
};

export const getZar8AssetPerformanceDelta = (series = []) => {
  if (series.length < 2) {
    return {
      deltaValue: 0,
      deltaPercentage: 0,
    };
  }

  const firstValue = parseNumericValue(series[0]?.value);
  const lastValue = parseNumericValue(series[series.length - 1]?.value);
  const deltaValue = lastValue - firstValue;

  return {
    deltaValue,
    deltaPercentage: firstValue > 0 ? (deltaValue / firstValue) * 100 : 0,
  };
};

const mapLedgerEntryToHistoryItem = (entry, marketAssets, fallbackType) => {
  const amountZar = parseNumericValue(
    entry?.capital ??
      entry?.valueInZar ??
      entry?.valueInLocalCurrency ??
      entry?.productId?.valueInLocalCurrency ??
      entry?.productId?.valueInZar ??
      entry?.productId?.value
  );
  const historyItem = buildZar8YieldPositionHistory({
    history: [
      {
        _id: entry?._id,
        type: fallbackType,
        createdAt: entry?.createdAt ?? entry?.startDate ?? null,
        valueInZar: amountZar,
        status: entry?.status ?? entry?.productId?.status ?? "success",
      },
    ],
    marketAssets,
  })[0];

  return historyItem || null;
};

export const buildZar8AssetYieldHistory = ({
  activeEntries = [],
  pendingEntries = [],
  yieldHistory = [],
  userTransactions = [],
  marketAssets = null,
}) => {
  const ledgerItems = [...activeEntries, ...pendingEntries]
    .map((entry) => mapLedgerEntryToHistoryItem(entry, marketAssets, "locked"))
    .filter(Boolean);

  const yieldHistoryItems = buildZar8YieldPositionHistory({
    history: yieldHistory,
    marketAssets,
  }).filter((item) =>
    ["locked", "unlocked", "yield"].includes(item?.type || "")
  );

  const transactionItems = buildZar8YieldPositionHistory({
    history: userTransactions,
    marketAssets,
  }).filter((item) =>
    ["locked", "unlocked", "yield"].includes(item?.type || "")
  );

  const seenKeys = new Set();
  const dedupedItems = [];

  [...yieldHistoryItems, ...transactionItems, ...ledgerItems].forEach(
    (item) => {
      const dedupeKey =
        item?.id ||
        `${item?.type}-${item?.createdAt || "recent"}-${item?.amountZar || 0}`;

      if (seenKeys.has(dedupeKey)) {
        return;
      }

      seenKeys.add(dedupeKey);
      dedupedItems.push(item);
    }
  );

  return dedupedItems
    .sort((firstItem, secondItem) => {
      const firstDate = dayjs(firstItem?.createdAt).valueOf() || 0;
      const secondDate = dayjs(secondItem?.createdAt).valueOf() || 0;

      return secondDate - firstDate;
    })
    .slice(0, 5);
};

export const emitZar8AssetPageEvent = (eventName, payload = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  const eventPayload = {
    product: ZAR8_ASSET_PAGE_PRODUCT,
    ...payload,
  };

  if (window.analytics?.track) {
    window.analytics.track(eventName, eventPayload);
  }

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: eventName,
      ...eventPayload,
    });
  }

  window.dispatchEvent(
    new CustomEvent("zar8-asset-page", {
      detail: {
        eventName,
        payload: eventPayload,
      },
    })
  );
};
